import { GameCategory, PeriodType } from '../../enums';
import { MarketConfig } from '../../interfaces';
import {
	extractNumber,
	extractSelection,
	getMarketSides,
	getPairPlayers,
	getPlayerPeriodStat,
	getPlayerStats,
	getTeamStat,
	resolveCondition,
	resolveGamePlayerProp,
	resolveMoneyline,
	resolveOvertimeGeneric,
	resolveSpread,
	resolveTeamTotal,
	resolveTotalPoints,
	resolveTotalPointsOddEven,
	sumPlayerPeriodsStat,
	TOTAL_TOUCHDOWNS,
} from '../../utils';
import { buildHalftimeFulltimeQuestion } from '../../utils/market/soccer.utils';
import {
	buildRaceToQuestion,
	buildSpreadQuestion,
	buildTeamTotalQuestion,
	buildTotalPointsOddEvenQuestion,
	buildTotalPointsQuestion,
	getPeriodScores,
	resolveRaceTo,
} from '../../utils/market/basketball.utils';
import {
	resolveComboPlayerStat,
	resolveCorrectScore,
	resolveEachPeriodThreshold,
	resolveEitherPlayerStat,
	resolveH2HPlayerStat,
	resolveH2HPlayerStatSpread,
	resolveHalftimeFulltime,
	resolveLongestPlayerStat,
	resolvePlayerPeriodThreshold,
	resolveTeamTotalOddEven,
	resolveTeamTotalStat,
} from '../../utils/market/football.utils';

const ALL_PERIOD = 'all';
const HALF1_PERIODS = [PeriodType.Period1, PeriodType.Period2];
const HALF2_PERIODS = [PeriodType.Period3, PeriodType.Period4];

export const footballConfig: Record<string, MarketConfig> = {
	// ────────────────────────────────────────────────────────────
	// Match Outcome — Moneyline
	// ────────────────────────────────────────────────────────────
	Moneyline: {
		buildQuestion: (m) => `Will ${m.selection} win the game?`,
		isFinalRequired: true,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Half Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 1st half?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'2nd Half Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 2nd half?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Half2),
	},
	'1st Quarter Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 1st quarter?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Quarter Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 2nd quarter?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Quarter Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 3rd quarter?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Quarter Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 4th quarter?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period4),
	},

	// ────────────────────────────────────────────────────────────
	// Point Spread
	// ────────────────────────────────────────────────────────────
	'Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Game),
		isFinalRequired: true,
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Half Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Half1),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'2nd Half Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Half2),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Half2),
	},
	'1st Quarter Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period1),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Quarter Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period2),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Quarter Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period3),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Quarter Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period4),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period4),
	},

	// ────────────────────────────────────────────────────────────
	// Game / Period Totals
	// ────────────────────────────────────────────────────────────
	'Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Game),
	},
	'Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Game),
		isFinalRequired: true,
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Game),
	},
	'1st Half Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Half1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'1st Half Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Half1),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Half1),
	},
	'2nd Half Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Half2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Half2),
	},
	'2nd Half Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Half2),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Half2),
	},
	'1st Quarter Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'1st Quarter Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Period1),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period1),
	},
	'2nd Quarter Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Period2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Quarter Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Period3),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Quarter Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Period4),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period4),
	},

	// ────────────────────────────────────────────────────────────
	// Total Touchdowns
	// ────────────────────────────────────────────────────────────
	'Total Touchdowns': {
		buildQuestion: (m) => `Will the game have at least ${m.points} total touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			return resolveTeamTotalStat(
				r,
				gc,
				'both',
				[ALL_PERIOD],
				TOTAL_TOUCHDOWNS,
				threshold,
				gc.isCompleted
			);
		},
	},
	'1st Quarter Total Touchdowns': {
		buildQuestion: (m) => `Will the 1st quarter have at least ${m.points} total touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			return resolveTeamTotalStat(
				r,
				gc,
				'both',
				[PeriodType.Period1],
				TOTAL_TOUCHDOWNS,
				threshold,
				gc.isScopeCompleted(PeriodType.Period1)
			);
		},
	},
	'2nd Quarter Total Touchdowns': {
		buildQuestion: (m) => `Will the 2nd quarter have at least ${m.points} total touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			return resolveTeamTotalStat(
				r,
				gc,
				'both',
				[PeriodType.Period2],
				TOTAL_TOUCHDOWNS,
				threshold,
				gc.isScopeCompleted(PeriodType.Period2)
			);
		},
	},
	'3rd Quarter Total Touchdowns': {
		buildQuestion: (m) => `Will the 3rd quarter have at least ${m.points} total touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			return resolveTeamTotalStat(
				r,
				gc,
				'both',
				[PeriodType.Period3],
				TOTAL_TOUCHDOWNS,
				threshold,
				gc.isScopeCompleted(PeriodType.Period3)
			);
		},
	},
	'4th Quarter Total Touchdowns': {
		buildQuestion: (m) => `Will the 4th quarter have at least ${m.points} total touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			return resolveTeamTotalStat(
				r,
				gc,
				'both',
				[PeriodType.Period4],
				TOTAL_TOUCHDOWNS,
				threshold,
				gc.isScopeCompleted(PeriodType.Period4)
			);
		},
	},
	'1st Half Total Touchdowns': {
		buildQuestion: (m) => `Will the 1st half have at least ${m.points} total touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			return resolveTeamTotalStat(
				r,
				gc,
				'both',
				HALF1_PERIODS,
				TOTAL_TOUCHDOWNS,
				threshold,
				gc.isScopeCompleted(PeriodType.Half1)
			);
		},
	},
	'2nd Half Total Touchdowns': {
		buildQuestion: (m) => `Will the 2nd half have at least ${m.points} total touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			return resolveTeamTotalStat(
				r,
				gc,
				'both',
				HALF2_PERIODS,
				TOTAL_TOUCHDOWNS,
				threshold,
				gc.isScopeCompleted(PeriodType.Half2)
			);
		},
	},

	// ────────────────────────────────────────────────────────────
	// Team Totals
	// ────────────────────────────────────────────────────────────
	'Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Game),
	},
	'Team Total Points Odd/Even': {
		buildQuestion: (m) => `Will ${m.selection}'s total points be even?`,
		isFinalRequired: true,
		resolve: (q, gc) => resolveTeamTotalOddEven(getPeriodScores, q, gc, PeriodType.Game),
	},
	'Team Total Touchdowns': {
		buildQuestion: (m) => `Will ${m.selection} score at least ${m.points} touchdowns as a team?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const team = extractSelection(q, 'Will ', ['score at least']);
			return resolveTeamTotalStat(
				r,
				gc,
				team,
				[ALL_PERIOD],
				TOTAL_TOUCHDOWNS,
				threshold,
				gc.isCompleted
			);
		},
	},
	'1st Quarter Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'1st Quarter Team Total Points Odd/Even': {
		buildQuestion: (m) => `Will ${m.selection}'s total points in the 1st quarter be even?`,
		resolve: (q, gc) => resolveTeamTotalOddEven(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'1st Half Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Half1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'1st Half Team Total Points Odd/Even': {
		buildQuestion: (m) => `Will ${m.selection}'s total points in the 1st half be even?`,
		resolve: (q, gc) => resolveTeamTotalOddEven(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'2nd Half Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Half2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Half2),
	},
	'2nd Half Team Total Points Odd/Even': {
		buildQuestion: (m) => `Will ${m.selection}'s total points in the 2nd half be even?`,
		resolve: (q, gc) => resolveTeamTotalOddEven(getPeriodScores, q, gc, PeriodType.Half2),
	},
	// ────────────────────────────────────────────────────────────
	// First/Last Team To Score
	// ────────────────────────────────────────────────────────────
	'First Team To Score': {
		buildQuestion: (m) => `Will ${m.selection} score first?`,
		resolve: (q, gc, r) => {
			const team = extractSelection(q, 'Will ', ['score first?']);
			const isHome = team === gc.homeTeam;
			return [
				resolveCondition(getTeamStat(r, isHome, 'first_team_to_score') === 1, gc.isCompleted),
			];
		},
	},
	'Last Team To Score': {
		buildQuestion: (m) => `Will ${m.selection} score the last touchdown of the game?`,
		isFinalRequired: true,
		resolve: (q, gc, r) => {
			const team = extractSelection(q, 'Will ', ['score the last touchdown of the game?']);
			const isHome = team === gc.homeTeam;
			return [
				resolveCondition(getTeamStat(r, isHome, 'last_team_to_score_td') === 1, gc.isCompleted),
			];
		},
	},
	// ────────────────────────────────────────────────────────────
	// Player Passing Props
	// ────────────────────────────────────────────────────────────
	'Player Passing Yards': {
		buildQuestion: (m) => `Will ${m.selection} throw for at least ${m.points} passing yards?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_passing_yards', ['throw for at least']),
	},
	'Player Passing Attempts': {
		buildQuestion: (m) => `Will ${m.selection} attempt at least ${m.points} passes?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_passing_attempts', ['attempt at least']),
	},
	'Player Passing Completions': {
		buildQuestion: (m) => `Will ${m.selection} complete at least ${m.points} passes?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_passing_completions', ['complete at least']),
	},
	'Player Passing Touchdowns': {
		buildQuestion: (m) => `Will ${m.selection} throw at least ${m.points} passing touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_passing_touchdowns', ['throw at least']),
	},
	'Player Longest Passing Completion': {
		buildQuestion: (m) => `Will ${m.selection}'s longest completion be at least ${m.points} yards?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveLongestPlayerStat(q, gc, r, 'longest_pass', 'period_stats'),
	},
	'1st Quarter Player Passing Yards': {
		buildQuestion: (m) =>
			`Will ${m.selection} throw for at least ${m.points} passing yards in the 1st quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['throw for at least']);
			const value = getPlayerPeriodStat(playerName, r, PeriodType.Period1, 'passing_yards');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Period1)), value];
		},
	},
	'1st Half Player Passing Yards': {
		buildQuestion: (m) =>
			`Will ${m.selection} throw for at least ${m.points} passing yards in the 1st half?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['throw for at least']);
			const value = sumPlayerPeriodsStat(playerName, r, HALF1_PERIODS, 'passing_yards');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Half1)), value];
		},
	},
	'Player Passing Yards Each Half': {
		buildQuestion: (m) =>
			`Will ${m.selection} throw for at least ${m.points} passing yards in each half?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveEachPeriodThreshold(q, gc, r, [HALF1_PERIODS, HALF2_PERIODS], 'passing_yards', [
				'throw for at least',
			]),
	},
	'Player Passing Yards Each Quarter': {
		buildQuestion: (m) =>
			`Will ${m.selection} throw for at least ${m.points} passing yards in each quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveEachPeriodThreshold(
				q,
				gc,
				r,
				[[PeriodType.Period1], [PeriodType.Period2], [PeriodType.Period3], [PeriodType.Period4]],
				'passing_yards',
				['throw for at least']
			),
	},
	'Player Passing Yards (Either)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);
			return `Will ${playerA} or ${playerB} throw for at least ${m.points} passing yards?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveEitherPlayerStat(
				q,
				gc,
				r,
				'player_passing_yards',
				'throw for at least .+ passing yards'
			),
	},
	'Player Passing Yards H2H Spread': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getMarketSides(m);
			return `Will ${playerA} throw for more passing yards than ${playerB} with a ${m.points} yard handicap?`;
		},
		isFinalRequired: true,
		resolve: (q, gc, r) =>
			resolveH2HPlayerStatSpread(
				q,
				gc,
				r,
				'player_passing_yards',
				'throw for more passing yards than'
			),
	},
	'Player Passing Yards (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);
			return `Will ${playerA} and ${playerB} combine for at least ${m.points} passing yards?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveComboPlayerStat(q, gc, r, 'player_passing_yards'),
	},
	'Player Passing + Rushing Yards': {
		buildQuestion: (m) => `Will ${m.selection} total at least ${m.points} passing + rushing yards?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_passing_+_rushing_yards', ['total at least']),
	},
	'Player Passing Yards H2H Moneyline': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getMarketSides(m);
			return `Will ${playerA} throw for more passing yards than ${playerB}?`;
		},
		isFinalRequired: true,
		resolve: (q, gc, r) =>
			resolveH2HPlayerStat(q, gc, r, 'player_passing_yards', 'throw for more passing yards than'),
	},

	// ────────────────────────────────────────────────────────────
	// Player Rushing Props
	// ────────────────────────────────────────────────────────────
	'Player Rushing Yards': {
		buildQuestion: (m) => `Will ${m.selection} rush for at least ${m.points} yards?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_rushing_yards', ['rush for at least']),
	},
	'Player Rushing Attempts': {
		buildQuestion: (m) => `Will ${m.selection} have at least ${m.points} rushing attempts?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_rushing_attempts', ['have at least']),
	},
	'1st Quarter Player Rushing Yards': {
		buildQuestion: (m) =>
			`Will ${m.selection} rush for at least ${m.points} yards in the 1st quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['rush for at least']);
			const value = getPlayerPeriodStat(playerName, r, PeriodType.Period1, 'rushing_yards');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Period1)), value];
		},
	},
	'Player Rushing Yards Each Quarter': {
		buildQuestion: (m) =>
			`Will ${m.selection} rush for at least ${m.points} yards in each quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveEachPeriodThreshold(
				q,
				gc,
				r,
				[[PeriodType.Period1], [PeriodType.Period2], [PeriodType.Period3], [PeriodType.Period4]],
				'rushing_yards',
				['rush for at least']
			),
	},
	'Player Rushing Yards Each Half': {
		buildQuestion: (m) => `Will ${m.selection} rush for at least ${m.points} yards in each half?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveEachPeriodThreshold(q, gc, r, [HALF1_PERIODS, HALF2_PERIODS], 'rushing_yards', [
				'rush for at least',
			]),
	},
	'Player Rushing + Receiving Yards': {
		buildQuestion: (m) =>
			`Will ${m.selection} total at least ${m.points} rushing + receiving yards?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_rushing_+_receiving_yards', ['total at least']),
	},
	'1st Quarter Player Rushing + Receiving Yards': {
		buildQuestion: (m) =>
			`Will ${m.selection} total at least ${m.points} rushing + receiving yards in the 1st quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['total at least']);
			const value =
				getPlayerPeriodStat(playerName, r, PeriodType.Period1, 'rushing_yards') +
				getPlayerPeriodStat(playerName, r, PeriodType.Period1, 'receiving_yards');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Period1)), value];
		},
	},
	'1st Half Player Rushing Yards': {
		buildQuestion: (m) =>
			`Will ${m.selection} rush for at least ${m.points} yards in the 1st half?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['rush for at least']);
			const value = sumPlayerPeriodsStat(playerName, r, HALF1_PERIODS, 'rushing_yards');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Half1)), value];
		},
	},
	'1st Quarter Player Rushing Attempts': {
		buildQuestion: (m) =>
			`Will ${m.selection} have at least ${m.points} rushing attempts in the 1st quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['have at least']);
			const value = getPlayerPeriodStat(playerName, r, PeriodType.Period1, 'rushing_attempts');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Period1)), value];
		},
	},
	'Player Rushing Yards (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);
			return `Will ${playerA} and ${playerB} combine for at least ${m.points} rushing yards?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveComboPlayerStat(q, gc, r, 'player_rushing_yards'),
	},
	'Player Rushing Yards H2H Moneyline': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getMarketSides(m);
			return `Will ${playerA} rush for more yards than ${playerB}?`;
		},
		isFinalRequired: true,
		resolve: (q, gc, r) =>
			resolveH2HPlayerStat(q, gc, r, 'player_rushing_yards', 'rush for more yards than'),
	},
	'Player Rushing Yards H2H Spread': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getMarketSides(m);
			return `Will ${playerA} rush for more yards than ${playerB} with a ${m.points} yard handicap?`;
		},
		isFinalRequired: true,
		resolve: (q, gc, r) =>
			resolveH2HPlayerStatSpread(q, gc, r, 'player_rushing_yards', 'rush for more yards than'),
	},

	// ────────────────────────────────────────────────────────────
	// Player Receiving Props
	// ────────────────────────────────────────────────────────────
	'Player Receiving Yards': {
		buildQuestion: (m) => `Will ${m.selection} receive for at least ${m.points} yards?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_receiving_yards', ['receive for at least']),
	},
	'Player Receptions': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} receptions?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_receptions', ['record at least']),
	},
	'1st Half Player Receiving Yards': {
		buildQuestion: (m) =>
			`Will ${m.selection} receive for at least ${m.points} yards in the 1st half?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['receive for at least']);
			const value = sumPlayerPeriodsStat(playerName, r, HALF1_PERIODS, 'receiving_yards');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Half1)), value];
		},
	},
	'1st Quarter Player Receiving Yards': {
		buildQuestion: (m) =>
			`Will ${m.selection} receive for at least ${m.points} yards in the 1st quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['receive for at least']);
			const value = getPlayerPeriodStat(playerName, r, PeriodType.Period1, 'receiving_yards');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Period1)), value];
		},
	},
	'1st Quarter Player Receptions': {
		buildQuestion: (m) =>
			`Will ${m.selection} record at least ${m.points} receptions in the 1st quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['record at least']);
			const value = getPlayerPeriodStat(playerName, r, PeriodType.Period1, 'receptions');
			return [resolveCondition(value >= threshold, gc.isScopeCompleted(PeriodType.Period1)), value];
		},
	},
	'Player Receiving Yards Each Quarter': {
		buildQuestion: (m) =>
			`Will ${m.selection} receive for at least ${m.points} yards in each quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveEachPeriodThreshold(
				q,
				gc,
				r,
				[[PeriodType.Period1], [PeriodType.Period2], [PeriodType.Period3], [PeriodType.Period4]],
				'receiving_yards',
				['receive for at least']
			),
	},
	'Player Receiving Yards (Either)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);
			return `Will ${playerA} or ${playerB} receive for at least ${m.points} yards?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveEitherPlayerStat(q, gc, r, 'player_receiving_yards', 'receive for at least .+ yards'),
	},
	'Player Receiving Yards H2H Spread': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getMarketSides(m);
			return `Will ${playerA} record more receiving yards than ${playerB} with a ${m.points} yard handicap?`;
		},
		isFinalRequired: true,
		resolve: (q, gc, r) =>
			resolveH2HPlayerStatSpread(
				q,
				gc,
				r,
				'player_receiving_yards',
				'record more receiving yards than'
			),
	},
	'Player Receiving Yards (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);
			return `Will ${playerA} and ${playerB} combine for at least ${m.points} receiving yards?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveComboPlayerStat(q, gc, r, 'player_receiving_yards'),
	},
	'Player Receiving Yards H2H Moneyline': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getMarketSides(m);
			return `Will ${playerA} record more receiving yards than ${playerB}?`;
		},
		isFinalRequired: true,
		resolve: (q, gc, r) =>
			resolveH2HPlayerStat(q, gc, r, 'player_receiving_yards', 'record more receiving yards than'),
	},

	// ────────────────────────────────────────────────────────────
	// Player Touchdown Props (over/under)
	// ────────────────────────────────────────────────────────────
	'Player Touchdowns': {
		buildQuestion: (m) => `Will ${m.selection} score at least ${m.points} touchdowns?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_touchdowns', ['score at least']),
	},
	'Player Touchdowns (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);
			return `Will ${playerA} and ${playerB} combine for at least ${m.points} touchdowns?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveComboPlayerStat(q, gc, r, 'player_touchdowns'),
	},
	'Player Touchdowns (Either)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);
			return `Will ${playerA} or ${playerB} score a touchdown?`;
		},
		resolve: (q, gc, r) =>
			resolveEitherPlayerStat(q, gc, r, 'player_touchdowns', 'score a touchdown'),
	},

	// ────────────────────────────────────────────────────────────
	// Player Kicking / Defensive Props
	// ────────────────────────────────────────────────────────────
	'Player Field Goals Made': {
		buildQuestion: (m) => `Will ${m.selection} make at least ${m.points} field goals?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_field_goals_made', ['make at least']),
	},
	'Player Extra Points Made': {
		buildQuestion: (m) => `Will ${m.selection} make at least ${m.points} extra points?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_extra_points_made', ['make at least']),
	},
	'Player Kicking Points': {
		buildQuestion: (m) => `Will ${m.selection} score at least ${m.points} kicking points?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_kicking_points', ['score at least']),
	},
	'Player Sacks': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} sacks?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_sacks', ['record at least']),
	},
	'Player Tackles': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} tackles?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_tackles', ['record at least']),
	},
	'Player Tackles + Assists': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} tackles + assists?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_tackles_+_assists', ['record at least']),
	},
	'Player Interceptions': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} interceptions?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_interceptions', ['record at least']),
	},
	// ────────────────────────────────────────────────────────────
	// Anytime Touchdown Scorer
	// ────────────────────────────────────────────────────────────
	'Anytime Touchdown Scorer': {
		buildQuestion: (m) => `Will ${m.selection} score a touchdown?`,
		resolve: (q, gc, r) =>
			resolvePlayerPeriodThreshold(
				q,
				gc,
				r,
				[ALL_PERIOD],
				PeriodType.Game,
				TOTAL_TOUCHDOWNS,
				['score a touchdown'],
				1
			),
	},
	'First Touchdown Scorer': {
		buildQuestion: (m) => `Will ${m.selection} score the first touchdown of the game?`,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['score the first touchdown of the game?']);
			return [
				resolveCondition(getPlayerStats(playerName, r).first_td_scorer === 1, gc.isCompleted),
			];
		},
	},
	'Last Touchdown Scorer': {
		buildQuestion: (m) => `Will ${m.selection} score the last touchdown of the game?`,
		isFinalRequired: true,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['score the last touchdown of the game?']);
			return [resolveCondition(getPlayerStats(playerName, r).last_td_scorer === 1, gc.isCompleted)];
		},
	},
	'1st Quarter Anytime Touchdown Scorer': {
		buildQuestion: (m) => `Will ${m.selection} score a touchdown in the 1st quarter?`,
		resolve: (q, gc, r) =>
			resolvePlayerPeriodThreshold(
				q,
				gc,
				r,
				[PeriodType.Period1],
				PeriodType.Period1,
				TOTAL_TOUCHDOWNS,
				['score a touchdown in the 1st quarter'],
				1
			),
	},
	'1st Quarter Player Touchdowns': {
		buildQuestion: (m) =>
			`Will ${m.selection} score at least ${m.points} touchdowns in the 1st quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolvePlayerPeriodThreshold(
				q,
				gc,
				r,
				[PeriodType.Period1],
				PeriodType.Period1,
				TOTAL_TOUCHDOWNS,
				['score at least']
			),
	},
	'1st Half Anytime Touchdown Scorer': {
		buildQuestion: (m) => `Will ${m.selection} score a touchdown in the 1st half?`,
		resolve: (q, gc, r) =>
			resolvePlayerPeriodThreshold(
				q,
				gc,
				r,
				HALF1_PERIODS,
				PeriodType.Half1,
				TOTAL_TOUCHDOWNS,
				['score a touchdown in the 1st half'],
				1
			),
	},
	'2nd Half Anytime Touchdown Scorer': {
		buildQuestion: (m) => `Will ${m.selection} score a touchdown in the 2nd half?`,
		resolve: (q, gc, r) =>
			resolvePlayerPeriodThreshold(
				q,
				gc,
				r,
				HALF2_PERIODS,
				PeriodType.Half2,
				TOTAL_TOUCHDOWNS,
				['score a touchdown in the 2nd half'],
				1
			),
	},
	'1st Half Player Touchdowns': {
		buildQuestion: (m) =>
			`Will ${m.selection} score at least ${m.points} touchdowns in the 1st half?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolvePlayerPeriodThreshold(q, gc, r, HALF1_PERIODS, PeriodType.Half1, TOTAL_TOUCHDOWNS, [
				'score at least',
			]),
	},
	'2nd Half Player Touchdowns': {
		buildQuestion: (m) =>
			`Will ${m.selection} score at least ${m.points} touchdowns in the 2nd half?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolvePlayerPeriodThreshold(q, gc, r, HALF2_PERIODS, PeriodType.Half2, TOTAL_TOUCHDOWNS, [
				'score at least',
			]),
	},

	// ────────────────────────────────────────────────────────────
	// Correct Score
	// ────────────────────────────────────────────────────────────
	'Correct Score': {
		buildQuestion: (m) => `Will the final score be ${m.selection_line}?`,
		isFinalRequired: true,
		resolve: (q, gc) => resolveCorrectScore(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Quarter Correct Score': {
		buildQuestion: (m) => `Will the 1st quarter score be ${m.selection_line}?`,
		resolve: (q, gc) => resolveCorrectScore(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'1st Half Correct Score': {
		buildQuestion: (m) => `Will the 1st half score be ${m.selection_line}?`,
		resolve: (q, gc) => resolveCorrectScore(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'Both Teams To Score': {
		buildQuestion: () => `Will both teams score in the game?`,
		isFinalRequired: true,
		resolve: (_q, gc) => {
			const { home, away, isCompleted } = getPeriodScores(gc, PeriodType.Game);
			return [resolveCondition(home > 0 && away > 0, isCompleted)];
		},
	},
	'Both Teams Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Game),
	},

	// ────────────────────────────────────────────────────────────
	// Race to N Points
	// ────────────────────────────────────────────────────────────
	'Race to 5 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 5, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 5),
	},
	'Race to 6 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 6, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 6),
	},
	'Race to 9 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 9, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 9),
	},
	'Race to 10 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 10, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 10),
	},
	'Race to 12 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 12, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 12),
	},
	'Race to 15 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 15, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 15),
	},
	'Race to 20 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 20, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 20),
	},
	'Race to 25 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 25, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 25),
	},
	'Race to 30 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 30, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 30),
	},
	'Race to 35 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 35, PeriodType.Game),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Game, 35),
	},

	// ────────────────────────────────────────────────────────────
	// Special Game Events
	// ────────────────────────────────────────────────────────────
	'Will There Be Overtime': {
		buildQuestion: () => `Will the game go to overtime?`,
		resolve: (_q, gc) => resolveOvertimeGeneric(gc, PeriodType.Period4, PeriodType.Period5),
	},
	'Halftime / Fulltime': {
		buildQuestion: (m) => buildHalftimeFulltimeQuestion(m.selection),
		isFinalRequired: true,
		resolve: (q, gc) => resolveHalftimeFulltime(getPeriodScores, q, gc),
	},
	'Will There Be A Safety': {
		buildQuestion: () => `Will there be a safety in the game?`,
		isFinalRequired: true,
		resolve: (_q, gc, r) => {
			const total = (r?.player_results ?? []).reduce(
				(sum: number, p: any) =>
					sum + (p.stats?.find((s: any) => s.period === 'all')?.stats?.safeties ?? 0),
				0
			);
			return [resolveCondition(total > 0, gc.isCompleted)];
		},
	},
};
void GameCategory;
